import * as React from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { AppUser } from '../../../../views/App/App';
import { Loader } from '../../../../components/Loader/Loader';
import { getClubs } from '../../../../helpers/service/admin/clubs';
import { getClubProgramme } from '../../../../helpers/service/admin/clubProgrammes';
import { AfterSchoolClubProgramme } from '../../../../models/clubProgramme';
import { ClubProgrammeClubFieldsOverrideEditor } from './ClubProgrammeClubFieldsOverrideEditor';
import { ClubProgrammePageHeader } from './components/ClubProgrammePageHeader';
import { ClubProgrammePageBody } from './components/ClubProgrammePageBody';

type Props = {
  user: AppUser;
};

export const ClubProgrammeClubFieldsOverridePage: React.FC<Props> = ({ user }) => {
  const { programmeId, programmeClubId } = useParams<{ programmeId: string; programmeClubId: string }>();
  const history = useHistory();
  const location = useLocation<any>();

  const [isLoading, setIsLoading] = React.useState(true);
  const [programme, setProgramme] = React.useState<AfterSchoolClubProgramme | null>(null);
  const [clubName, setClubName] = React.useState('Club');
  const [error, setError] = React.useState('');

  const goBack = React.useCallback(() => {
    const backTo = location?.state?.from || {
      pathname: `/clubProgrammes/${programmeId}/manageclubs`,
      search: ''
    };
    history.push(backTo);
  }, [history, location, programmeId]);

  const load = React.useCallback(() => {
    if (!programmeId) return;

    setIsLoading(true);
    setError('');

    getClubProgramme(user, String(programmeId))
      .then((p: AfterSchoolClubProgramme) => {
        setProgramme(p);
        const pc = ((p as any)?.clubs || []).find((item: any) => String(item?._id || item?.id) === String(programmeClubId));
        const clubId = String(pc?.clubId || '');

        if (!clubId) {
          setClubName('Club');
          setIsLoading(false);
          return;
        }

        getClubs(user, {
          limit: 1,
          skip: 0,
          where: { _id: clubId }
        } as any)
          .then((items: any[]) => {
            const club = (items || [])[0];
            setClubName(String(club?.name || 'Club'));
            setIsLoading(false);
          })
          .catch(() => {
            setClubName('Club');
            setIsLoading(false);
          });
      })
      .catch(e => {
        console.error('ClubProgrammeClubFieldsOverridePage load error', e);
        setError('Failed to load programme club.');
        setProgramme(null);
        setIsLoading(false);
      });
  }, [user, programmeId, programmeClubId]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (isLoading) return <Loader />;

  const programmeClub = ((programme as any)?.clubs || []).find(
    (item: any) => String(item?._id || item?.id) === String(programmeClubId)
  );
  const canEdit = String((programme as any)?.status || 'DRAFT') === 'DRAFT';

  return (
    <div
      className="mt-3"
      style={{
        height: 'calc(100vh - 110px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}
    >
      <ClubProgrammePageHeader
        title="Override booking fields"
        programmeName={`${String((programme as any)?.name || 'Programme')} - ${clubName}`}
        onBackClick={goBack}
      />

      <ClubProgrammePageBody>
        {error ? <div className="alert alert-danger">{error}</div> : null}
        {!error && !programmeClub ? <div className="alert alert-warning">Programme club was not found.</div> : null}
        {programme && programmeClub ? (
          <ClubProgrammeClubFieldsOverrideEditor
            user={user}
            programmeId={String(programmeId || '')}
            programmeClubId={String(programmeClubId || '')}
            programmeDefaultFields={Array.isArray((programme as any)?.defaultFields) ? (programme as any).defaultFields : []}
            programmeFormDefinition={Number((programme as any)?.formSchemaVersion) === 2 ? (programme as any)?.formDefinition : undefined}
            currentOverride={(programmeClub as any)?.fieldsOverride || null}
            canEdit={canEdit}
            defaultTemplateName={`${clubName} booking template`}
            onClose={goBack}
            onSaved={(updatedProgramme: any) => {
              if (updatedProgramme) setProgramme(updatedProgramme);
            }}
          />
        ) : null}
      </ClubProgrammePageBody>
    </div>
  );
};
